import axios from 'axios';
import { showToast } from '../reducers/uiReducer';
import { setAuthState } from '../reducers/authReducer';

import { apiClient, authClient } from './apiClient';
import {
  setUserDocuments,
  clearUserDocuments,
} from '../reducers/documentsReducer';
import {
  setUserDatasets,
  clearUserDatasets,
} from '../reducers/datasetsReducer';
import {
  setUserPrompts, clearUserPrompts, updateSavedPrompt,
  insertSavedPrompt,
  deleteSavedPrompt,
} from '../reducers/promptsReducer';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Add Axios interceptor to handle 403 and 401 errors globally
apiClient.interceptors.response.use(
  (response) => response, // Pass through successful responses
  (error) => {
    if (error.response) {
      if (
        error.response.status === 403 &&
        error.response.data.message === 'Invalid or expired token.'
      ) {
        // Clear token and redirect with error identifier
        localStorage.removeItem('token');
        window.location.href = '/login?error=token_expired';
      } else if (
        error.response.status === 401 &&
        error.response.data.message === 'Access Denied. No valid token provided.'
      ) {
        // Clear token and redirect with error identifier
        localStorage.removeItem('token');
        window.location.href = '/login?error=token_invalid';
      }
    }
    return Promise.reject(error); // Reject the error for further handling
  }
);

authClient.interceptors.response.use(
  (response) => response, // Pass through successful responses
  (error) => {
    if (error.response) {
      if (
        error.response.status === 403 &&
        error.response.data.message === 'Invalid or expired token.'
      ) {
        // Clear token and redirect with error identifier
        localStorage.removeItem('token');
        window.location.href = '/login?error=token_expired';
      } else if (
        error.response.status === 401 &&
        error.response.data.message === 'Access Denied. No valid token provided.'
      ) {
        // Clear token and redirect with error identifier
        localStorage.removeItem('token');
        window.location.href = '/login?error=token_invalid';
      }
    }
    return Promise.reject(error); // Reject the error for further handling
  }
);

export const sendMessage = async (chatContext, type = 'general', dispatch) => {
  console.log('🔍 Sending chat request:', { chatContext, type });

  try {
    //await delay(2000); // Keep the delay for UX

    // Ensure chatContext is an array, default to empty array if undefined
    const chatMessages = Array.isArray(chatContext) ? chatContext : [];

    // Filter out invalid roles and extract messages correctly
    const filteredMessages = chatMessages
      .filter((msg) => ['system', 'assistant', 'user'].includes(msg.role)) // Remove invalid roles
      .map((msg) => ({
        role: msg.role, // Should be "user" or "assistant"
        content: msg.content.map((c) => c.text).join(' '), // Extract text
        promptItems: msg.promptItems || [], // Default to empty array if not present
      }));

    const response = await apiClient.post('/messages', {
      chatPrompt: filteredMessages, // Send as an array
      type,
    });

    const responseData = response.data;

    if (type === 'structured_chart_data' && responseData?.data) {
      console.log('Received structured chart data:', responseData);
      return responseData; // Return structured JSON for D3
    }

    console.log('Received general chat response:', responseData.text);
    return responseData.text;
  } catch (error) {
    console.error('Failed to send message:', error);
    throw error;
  }
};

//DOCUMENTS

export const fetchDocuments = async (dispatch) => {
  try {
    //await delay(500); // Simulated delay for UI loading
    const response = await apiClient.get(`/documents`);

    return response.data;
  } catch (error) {
    dispatch(
      showToast({ message: 'Error sending message!: ', severity: 'error' }),
    );
    console.error('Failed to send message:', error);
    throw error;
  }
};

export const fetchDocument = async (documentId, dispatch) => {
  try {
    //await delay(500); // Simulated delay for UI loading
    const response = await apiClient.get(`/documents/${documentId}`);
    return response.data;
  } catch (error) {
    dispatch(
      showToast({ message: 'Error sending message!: ', severity: 'error' }),
    );
    console.error('Failed to send message:', error);
    throw error;
  }
};

export const postDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('token'); // Retrieve the token from localStorage

  try {
    //await delay(2000); // Simulated delay for UI loading
    const response = await apiClient.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`, // Add the Bearer token
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to upload document:', error);
    throw error;
  }
};

export const deleteDocument = async (documentId, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.delete(`/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete document:', error);
    throw error;
  }
};

export const queryDocument = async (documentId, query, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.post(`/documents/query/${documentId}`, {
      query,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to query document:', error);
    throw error;
  }
};

export const fetchAnalysisResults = async (documentId, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.get(`/documents/results/${documentId}`);

    return response.data;
  } catch (error) {
    dispatch(
      showToast({ message: 'Error sending message!: ', severity: 'error' }),
    );
    console.error('Failed to send message:', error);
    throw error;
  }
};


//DATASETS

export const fetchDatasets = async (dispatch) => {
  try {
    //await delay(1000); // Add a 2-second delay
    const response = await apiClient.get('/datasets');
    return response.data;
  } catch (error) {
    console.error('Error fetching datasets:', error);
    throw error;
  }
}

export const fetchDataset = async (datasetId, dispatch) => {
  try {
    await delay(1000); // Add a 2-second delay
    const response = await apiClient.get(`/datasets/${datasetId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching dataset:', error);
    throw error;
  }
}


export const queryDataset = async (documentId, query, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.post(`/datasets/query/${documentId}`, {
      query,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to query document:', error);
    throw error;
  }
};

export const fetchDatasetResults = async (documentId, dispatch) => {
  try {
    await delay(2000); // Add a 2-second delay
    const response = await apiClient.get(`/documents/results/${documentId}`);

    return response.data;
  } catch (error) {
    dispatch(
      showToast({ message: 'Error sending message!: ', severity: 'error' }),
    );
    console.error('Failed to send message:', error);
    throw error;
  }
};




//PROMPTS

export const fetchPrompts = async (dispatch) => {
  await delay(1000); // Add a 2-second delay
  const token = localStorage.getItem('token');

  try {
    //await delay(1000); // Add a 2-second delay
    const response = await apiClient.get('/prompts', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching prompts:', error);
    throw error;
  }
};

export const updatePrompt = async (promptId, updatedPrompt, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.put(`/prompts/${promptId}`, updatedPrompt);
    dispatch(setUserPrompts(response.data)); // Dispatch the action to update the prompt in auth state
    return response.data;
  } catch (error) {
    console.error('Failed to update prompt:', error);
    throw error;
  }
};

export const createPrompt = async (newPrompt, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.post('/prompts', newPrompt);
    dispatch(insertSavedPrompt(response.data));
    dispatch(insertSavedPrompt(response.data));
    return response.data;
  } catch (error) {
    console.error('Failed to create prompt:', error);

    throw error;
  }
};

export const deletePrompt = async (promptId, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.delete(`/prompts/${promptId}`);
    dispatch(deleteSavedPrompt(promptId)); // Pass the promptId to the action
    return response.data;
  } catch (error) {
    console.error('Failed to delete prompt:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId, updatedProfile, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.put(`/users/${userId}`, updatedProfile);
    //dispatch(setAuthState({ user: response.data })); // Update the user in auth state
    return response.data;
  } catch (error) {
    console.error('Failed to update user profile:', error);
    throw error;
  }
};

export const login = async (email, password) => {
  try {
    const response = await authClient.post('/login', { email, password });
    const token = response.data.token;
    localStorage.setItem('token', token); // Store the token in localStorage
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Failed to login');
    }
  }
};

export const register = async (email, password, firstname, lastname) => {
  try {
    const response = await authClient.post('/register', {
      email,
      password,
      firstname,
      lastname,
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Failed to register');
    }
  }
};

export const checkAuth = async (dispatch) => {
  const token = localStorage.getItem('token');
  const currentPath = window.location.pathname;

  if (token) {

    try {
      const response = await authClient.get('/check-auth', {
        headers: { Authorization: `Bearer ${token}` },
      });

      // dispatch(setUserDocuments(response.data.user.preferences.documents));
      // dispatch(setUserDatasets(response.data.user.preferences.dataset));
      // dispatch(setUserPrompts(response.data.user.preferences.prompts));
      dispatch(
        setAuthState({
          user: response.data.user,
          isAuthenticated: true,
          //token,
        }),
      );
    } catch (error) {
      console.error('Failed to check authentication:', error);
      // Clear the token from localStorage
      localStorage.removeItem('token');
      //Clear the user from the state
      dispatch(
        setAuthState({ user: null, isAuthenticated: false }),
      );
      dispatch(clearUserDocuments());
      dispatch(clearUserDatasets);
      dispatch(clearUserPrompts());
      // Redirect to the login page if not already there
      if (currentPath !== '/login') {
        window.location.href = '/login';
      }
      // Dispatch the error message
      dispatch(showToast({ message: error.message, severity: 'error' }));
    }
  } else {
    dispatch(setAuthState({ user: null, isAuthenticated: false }));
    if (currentPath !== '/login') {
      //window.location.href = '/login';
    }
  }
};

export const logout = async (dispatch) => {
  localStorage.removeItem('token');
  dispatch(setAuthState({ user: null, isAuthenticated: false }));
  dispatch(clearUserDocuments());
  dispatch(clearUserDatasets);
  dispatch(clearUserPrompts());
  window.location.href = '/login';
};

export const registrationStatus = async (dispatch) => {
  try {
    //await delay(1000); // Add a 1-second delay
    const response = await apiClient.get('/registration/registration-status');
    return response.data;
  } catch (error) {
    console.error('Failed to check registration:', error);
    throw error;
  }
};

export const checkInvitationCode = async (code, dispatch) => {
  try {
    const response = await apiClient.post('/registration/check-code', { code });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Failed to check code');
    }
  }
};

export const updateUserPasscode = async (userId, newPassword) => {
  const tempToken = localStorage.getItem('tempToken');

  try {
    const headers = { Authorization: `Bearer ${tempToken}` };

    const response = await apiClient.put(
      `/registration/${userId}/password-reset`,
      { newPassword },
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update user passcode:', error);
    throw error;
  }
};

export const sendUserInvitation = async (userDetails, dispatch) => {
  try {
    // await delay(2000); // Add a 2-second delay
    const response = await apiClient.post(`/admin/send-invite`, userDetails);
    return response.data;
  } catch (error) {
    console.error('Failed to send invite:', error);
    throw error;
  }
};

export const fetchUserList = async (dispatch) => {
  try {
    // await delay(1000); // Add a 2-second delay
    const response = await apiClient.get('/admin/users');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user list:', error);
    throw error;
  }
};

export const fetchUser = async (userId, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.get(`/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user details:', error);
    throw error;
  }
};

export const updateUser = async (userId, updatedUser, dispatch) => {
  try {
    //await delay(1000); // Add a 2-second delay
    const response = await apiClient.put(`/admin/users/${userId}`, updatedUser);
    return response.data;
  } catch (error) {
    console.error('Failed to update user:', error);
    throw error;
  }
};

export const deleteUser = async (userId, dispatch) => {
  try {
    //await delay(2000); // Add a 2-second delay
    const response = await apiClient.delete(`/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete user:', error);
    throw error;
  }
};
